import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export interface LinhaDetalhe {
  telefone: string;
  plano: string;
  gb: number;
  valor: number;
  m: number | null;
  estado: string;
  servicos: string[];
}

export interface PlantaVivoSnapshot {
  id: string;
  lead_id: string;
  cnpj: string;
  created_at: string;
  updated_at: string;
  uploaded_by: string | null;
  total_linhas: number;
  valor_total: number;
  gb_total: number;
  linhas_detalhadas: LinhaDetalhe[];
  dados_brutos: Record<string, unknown>;
}

export function usePlantaVivoSnapshot(leadId: string | null | undefined) {
  const queryClient = useQueryClient();

  const { data: snapshot, isLoading, refetch } = useQuery({
    queryKey: ['planta-vivo-snapshot', leadId],
    queryFn: async () => {
      if (!leadId) return null;

      const { data, error } = await supabase
        .from('planta_vivo_snapshot')
        .select('*')
        .eq('lead_id', leadId)
        .maybeSingle();

      if (error) throw error;
      
      // Parse linhas_detalhadas from JSONB
      if (data) {
        return {
          ...data,
          linhas_detalhadas: (data.linhas_detalhadas as unknown as LinhaDetalhe[]) || [],
        } as PlantaVivoSnapshot;
      }
      
      return null;
    },
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const saveMutation = useMutation({
    mutationFn: async (dados: {
      lead_id: string;
      cnpj: string;
      total_linhas: number;
      valor_total: number;
      gb_total: number;
      linhas_detalhadas: LinhaDetalhe[];
      dados_brutos: Record<string, unknown>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Check if snapshot exists
      const { data: existing } = await supabase
        .from('planta_vivo_snapshot')
        .select('id')
        .eq('lead_id', dados.lead_id)
        .maybeSingle();

      const linhasJson = JSON.parse(JSON.stringify(dados.linhas_detalhadas)) as Json;
      const dadosBrutosJson = JSON.parse(JSON.stringify(dados.dados_brutos)) as Json;

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('planta_vivo_snapshot')
          .update({
            cnpj: dados.cnpj,
            total_linhas: dados.total_linhas,
            valor_total: dados.valor_total,
            gb_total: dados.gb_total,
            linhas_detalhadas: linhasJson,
            dados_brutos: dadosBrutosJson,
            uploaded_by: user?.id || null,
            updated_at: new Date().toISOString(),
          })
          .eq('lead_id', dados.lead_id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('planta_vivo_snapshot')
          .insert({
            lead_id: dados.lead_id,
            cnpj: dados.cnpj,
            total_linhas: dados.total_linhas,
            valor_total: dados.valor_total,
            gb_total: dados.gb_total,
            linhas_detalhadas: linhasJson,
            dados_brutos: dadosBrutosJson,
            uploaded_by: user?.id || null,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Planta Vivo importada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['planta-vivo-snapshot', leadId] });
      // Invalidar métricas e lista de leads para atualizar indicadores de fatura
      queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
      queryClient.invalidateQueries({ queryKey: ['meus-leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-carteira-supervisor'] });
    },
    onError: (error: Error) => {
      console.error('Erro ao salvar Planta Vivo:', error);
      toast.error(`Erro ao salvar: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!leadId) throw new Error('Lead ID não encontrado');

      const { error } = await supabase
        .from('planta_vivo_snapshot')
        .delete()
        .eq('lead_id', leadId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Dados removidos');
      queryClient.invalidateQueries({ queryKey: ['planta-vivo-snapshot', leadId] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover: ${error.message}`);
    },
  });

  return {
    snapshot,
    isLoading,
    refetch,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    remove: deleteMutation.mutate,
    isRemoving: deleteMutation.isPending,
  };
}
